import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import ReactAudioPlayer from 'react-audio-player';
import {
  colors,
  helpers,
  typography,
  animations,
  mediaQueries as mq,
} from 'src/styles';
import { rgba } from 'polished';
import Button from 'src/components/Button';
import Image from 'src/components/Image';
import Section from 'src/components/Section';
import ConditionalRender from 'src/components/ConditionalRender';
import Parallax from 'src/components/Parallax';

const AudioPlayerElement = styled(ReactAudioPlayer)`
  margin-top: 100px;
  display: none !important;
`;

const PlayerUiWrapper = styled.div`
  background: ${({ bgColor }) =>
    bgColor === 'lightBlue' ? colors.bgColor : colors.blueLighten};
  display: flex;
  max-width: 800px;
  width: 100%;
  position: relative;
  ${({ hasBgImage }) =>
    hasBgImage ? 'box-shadow: 0px 5px 10px 0 rgba(0, 0, 0, .3);' : ''}
  ${mq.mediumAndBelow} {
    padding-top: 32px;
    align-items: center;
  }
`;

const ImageWrap = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  ${helpers.responsiveStyles('width', 170, 170, 170, 80)}
  .gatsby-image-wrapper {
    height: 100%;
  }
`;

const PlayerContentArea = styled.div`
  flex-grow: 1;
  overflow: hidden;
`;

const PlayerHeadline = styled.div`
  ${typography.bodyLarge}
  ${helpers.responsiveStyles('padding-left', 40, 30, 20, 14)}
  ${helpers.responsiveStyles('padding-right', 40, 30, 20, 14)}
  padding-top: .35em;
  padding-bottom: 0.35em;
  background: ${colors.green};
  color: ${colors.white};
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${mq.mediumAndBelow} {
    display: none;
  }
  ${({ mobile }) =>
    mobile &&
    `
    display: none;
    ${mq.mediumAndBelow} {
      ${typography.body}
      display: flex;
      position: absolute;
      top: 0;
      height: 32px;
      width: 100%;
      font-weight: 500;
    }
  `}
`;
const PlayerContent = styled.div`
  ${helpers.responsiveStyles('padding-left', 40, 30, 20, 14)}
  ${helpers.responsiveStyles('padding-right', 40, 30, 20, 14)}
  ${helpers.responsiveStyles('padding-top', 24, 22, 16, 14)}
  ${helpers.responsiveStyles('padding-bottom', 24, 22, 16, 14)}
  flex-shrink: 1;
  overflow: hidden;
`;
const PlayerTitle = styled.h5`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  max-width: 100%;
  margin-top: 0;
  ${helpers.responsiveStyles('margin-bottom', 24, 20, 18, 16)}
`;

const PlayerControls = styled.div`
  display: flex;
  align-items: center;
`;

const ScrubberWrap = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  padding-left: 14px;
`;

const Time = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  ${typography.bodySmall}
  font-weight: 500;
  padding-top: 2px;
  ${({ alignment }) => alignment && `text-align: ${alignment};`}
  width: ${({ duration }) => (duration >= 3600 ? `5em` : `3.25em`)};
`;

const ScrubberInput = styled.input`
  position: absolute;
  z-index: 2;
  width: calc(100% + 12px);
  height: 11px;
  top: -7px;
  left: -6px;
  appearance: none;
  background: transparent;

  :focus {
    box-shadow: none;
    outline: none;
  }

  ::-webkit-slider-thumb {
    appearance: none;
    cursor: grab;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: ${colors.textColor};
    &:active {
      cursor: grabbing;
    }
  }

  ::-moz-range-thumb {
    appearance: none;
    cursor: grab;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: ${colors.textColor};
  }
`;

const ScrubberTrack = styled.div`
  height: 1px;
  width: 100%;
  background: ${colors.lightTextColor};
  transition: transform ${animations.mediumSpeed} ease-in-out,
    background ${animations.mediumSpeed} ease-in-out;
`;

const ScrubberProgress = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  height: 1px;
  width: ${({ currentTime, duration }) =>
    `${((currentTime / duration) * 100).toPrecision(4)}%`};
  background: ${colors.green};
`;

const TransparentScrim = styled.div`
  background: rgba(255, 255, 255, 0.5);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const ParallaxWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: calc(100% / 3 * 2);
  height: 100%;
  overflow: hidden;

  ${mq.largeAndBelow} {
    width: 100%;
  }

  & > div,
  .gatsby-image-wrapper {
    width: 100%;
  }
`;

const Scrubber = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  background: transparent;
  margin: 0 20px;
  flex-grow: 1;
  &:hover {
    ${ScrubberTrack} {
      transform: scaleY(3);
      background: ${rgba(colors.textColor, 0.2)};
    }
  }
`;

const AudioPlayer = (props) => {
  const {
    image,
    headline,
    title,
    bgColor,
    file: {
      localFile: {
        publicURL: src
      }
    },
    prevBgColor,
    nextBgColor,
    pageColors,
    backgroundImage,
  } = props;

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(null);
  const [muted, setMuted] = useState(false);
  const player = useRef();

  useEffect(() => {
    const currentPlayer = player.current.audioEl.current;

    currentPlayer.addEventListener('loadeddata', (e) => {
      setDuration(e.target.duration);
    });

    currentPlayer.addEventListener('timeupdate', handleSetCurrentTime);

    return () => {
      currentPlayer.removeEventListener('timeupdate', handleSetCurrentTime);
    };
  }, [player]);

  const handleSetCurrentTime = (event) => {
    setCurrentTime(event.target.currentTime);
  };

  const playPause = () => {
    if (playing) {
      player.current.audioEl.current.pause();
    } else {
      player.current.audioEl.current.play();
    }
    setPlaying(!playing);
  };

  const mute = () => {
    setMuted(!muted);
  };

  const secondsTohhmmss = (totalSeconds, duration) => {
    let hours = Math.floor(totalSeconds / 3600);
    let minutes = Math.floor((totalSeconds - hours * 3600) / 60);
    let seconds = totalSeconds - hours * 3600 - minutes * 60;

    let hasHours = duration >= 3600 || totalSeconds >= 3600;

    // round seconds
    seconds = Math.round(seconds * 100) / 100;

    let result =
      (hasHours ? (hours < 10 ? '0' + hours : hours) + ':' : '') +
      (minutes < 10 ? '0' + minutes : minutes) +
      ':' +
      (seconds < 10 ? '0' + seconds : seconds);
    return result;
  };

  const handleChange = (event) => {
    player.current.audioEl.current.currentTime = event.target.value;
  };

  return ( src &&
    <Section
      bgColor="lightBlue"
      pageColors={pageColors}
      prevBgColor={prevBgColor}
      nextBgColor={nextBgColor}
      evenPadding={true}
    >
      {backgroundImage && (
        <ParallaxWrapper>
          <Parallax posYStart={0} posYEnd={-25} scrollUnit="%">
            <Image image={{image: backgroundImage}} animate={false} />
          </Parallax>
          <TransparentScrim />
        </ParallaxWrapper>
      )}

      <PlayerUiWrapper bgColor={bgColor} hasBgImage={backgroundImage}>
        <PlayerHeadline mobile={true}>
          {headline}{' '}
          <Button
            onClick={mute}
            icon={!muted ? 'volume_up' : 'volume_off'}
            size="tiny"
            shape="circle"
          />
        </PlayerHeadline>

        <ConditionalRender condition={image}>
          <ImageWrap>
            <Image image={{image}} animate={false} />
          </ImageWrap>
        </ConditionalRender>

        <PlayerContentArea>
          <PlayerHeadline>
            {headline}{' '}
            <Button
              onClick={mute}
              icon={!muted ? 'volume_up' : 'volume_off'}
              size="small"
              shape="circle"
            />
          </PlayerHeadline>
          <PlayerContent>
            <PlayerTitle>{title}</PlayerTitle>
            <PlayerControls>
              <Button
                onClick={playPause}
                icon={playing ? 'pause' : 'play_arrow'}
                size="tiny"
                shape="circle"
              />
              <ScrubberWrap>
                <Time duration={duration} alignment="right">
                  {secondsTohhmmss(Math.round(currentTime), duration)}
                </Time>
                <Scrubber>
                  <ScrubberInput
                    type="range"
                    min={0}
                    max={duration}
                    value={currentTime}
                    onChange={handleChange}
                  />
                  <ScrubberTrack>
                    <ScrubberProgress
                      currentTime={currentTime}
                      duration={duration}
                    />
                  </ScrubberTrack>
                </Scrubber>
                <Time duration={duration}>
                  {secondsTohhmmss(Math.round(duration))}
                </Time>
              </ScrubberWrap>
            </PlayerControls>
          </PlayerContent>
        </PlayerContentArea>
      </PlayerUiWrapper>

      <AudioPlayerElement
        ref={player}
        controls
        muted={muted}
        src={src}
      />
    </Section>
  );
}

export default AudioPlayer;
